import { observer } from 'mobx-react-lite'
import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { getLabelAsNumberInternationalFormat, localTime, offsetFromUTC } from 'shared/lib'
import { AvatarGroup, Icon, Tooltip, Typography } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { isInQuietHoursTimeframe } from 'entities/QuietHours'
import { useContactsProfileCardContext } from 'widgets/ContactsDetails/ui/ContactsProfileCard/context'
import styles from './styles.module.scss'

export const ContactProfileInfo = observer(() => {
  const { featureFlagsStore } = useEntitiesContext()
  const store = useContactsProfileCardContext()
  const {
    contact,
    hasContact,
    currentContactsAvatarInfo,
    hasParticipants,
    isNameEqualsPhone,
    timezone,
    areaCodeInfo,
    contactsCount,
    name,
    number,
    isQuietHoursEnabled,
    quietHours,
  } = store
  const [localTimeValue, setLocalTimeValue] = useState('')

  useEffect(() => {
    if (timezone) {
      setLocalTimeValue(localTime(timezone))

      const interval: ReturnType<typeof setInterval> = setInterval(() => {
        setLocalTimeValue(localTime(timezone))
      }, 1000 * 60)

      return () => {
        clearInterval(interval)
      }
    }
  }, [contact])

  if (!hasContact) return null

  return (
    <div className={styles.wrap}>
      <AvatarGroup items={currentContactsAvatarInfo} size={40} />
      <div
        className={classNames(styles.info, {
          [styles.withoutTime]: !timezone,
          [styles.isNameEqualsPhone]: isNameEqualsPhone,
        })}
      >
        <div className={styles.user}>
          <div className={styles.wrapName}>
            <Typography
              className={classNames(styles.name, {
                [styles.is_participants]: hasParticipants,
              })}
              variant='body-rg-medium'
              ariaLabel={'name'}
            >
              {getLabelAsNumberInternationalFormat(name)}
            </Typography>
            {hasParticipants && (
              <Typography variant='body-rg-medium' ariaLabel={'more'}>
                {hasParticipants && `+ ${contactsCount - 1} more`}
              </Typography>
            )}
          </div>

          {timezone && (
            <Tooltip
              type={'description'}
              label={`UTC ${offsetFromUTC(timezone)} ${areaCodeInfo}`}
              desc={'Timezone is defined by the contact’s area code'}
              width={170}
              placement={'top-end'}
            >
              {featureFlagsStore.quiet_hours &&
              isQuietHoursEnabled &&
              isInQuietHoursTimeframe(
                timezone,
                quietHours.quietHoursAllowedTimeFrom,
                quietHours.quietHoursAllowedTimeTo
              ) ? (
                <div className={styles.wrapTimeValue}>
                  <Icon icon='moon' fontSize={12} color={'var(--deep-purple-70)'} />{' '}
                  <Typography
                    variant='body-xs-regular'
                    tag='div'
                    ariaLabel={'localTimeValue'}
                    color={'var(--deep-purple-80)'}
                  >
                    {localTimeValue}
                  </Typography>
                </div>
              ) : (
                <Typography variant='text-input-helper' tag='div' ariaLabel={'localTimeValue'}>
                  {localTimeValue}
                </Typography>
              )}
            </Tooltip>
          )}
        </div>

        {!isNameEqualsPhone && (
          <Typography
            className={styles.name}
            variant='body-sm-regular'
            tag='div'
            ariaLabel={'formatted_number'}
          >
            {hasParticipants
              ? `${contactsCount} contacts`
              : number && getLabelAsNumberInternationalFormat(number)}
          </Typography>
        )}
      </div>
    </div>
  )
})
